import { useState, useCallback } from 'react';
import axios from 'axios';
import { showToast } from '../../../contexts/ToastNotification';

export function useOrderDetails() {
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);


    const formatCurrency = useCallback((amount) => {
        return new Intl.NumberFormat('it-IT', { 
            style: 'currency', 
            currency: 'EUR' 
        }).format(amount || 0);
    }, []);

    // Formatta data
    const formatDate = useCallback((dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleString('it-IT');
    }, []);

    // Fetch dettaglio ordine
    const fetchOrderDetail = useCallback(async (orderId) => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await axios.get(`/api/admin/orders/${orderId}`, {
                withCredentials: true
            });
            
            if (response.data.success) {
                const order = response.data.order;
                // Arricchisci l'ordine con dati formattati
                const enrichedOrder = {
                    ...order,
                    formattedTotal: formatCurrency(order.totale),
                    formattedSubtotal: formatCurrency(order.subtotale),
                    formattedShipping: formatCurrency(order.costo_spedizione),
                    formattedDate: formatDate(order.data_creazione),
                    items: order.items?.map(item => ({
                        ...item,
                        formattedPrice: formatCurrency(item.prezzo_unitario),
                        formattedTotal: formatCurrency(item.prezzo_totale)
                    }))
                };
                
                setSelectedOrder(enrichedOrder);
                return enrichedOrder;
            } else {
                throw new Error(response.data.message || 'Errore nel caricamento');
            }
        } catch (err) {
            const message = err.response?.data?.message || err.message;
            setError(message);
            showToast(`Errore: ${message}`, 'error');
            return null;
        } finally {
            setLoading(false);
        }
    }, [formatCurrency, formatDate]);

    // Resetta l'ordine selezionato
    const resetSelectedOrder = useCallback(() => {
        setSelectedOrder(null);
        setError(null);
    }, []);

    return {
        // State
        selectedOrder,
        loading,
        error,
        
        // Actions
        fetchOrderDetail,
        resetSelectedOrder,
        
        // Helpers
        formatCurrency,
        formatDate
    };
};

    export const useOrderGrouping = () => {
        // State
        const [availableOrders, setAvailableOrders] = useState([]);
        const [optimizedRoute, setOptimizedRoute] = useState(null);
        const [loadingRoute, setLoadingRoute] = useState(false);
        const [loadingOrders, setLoadingOrders] = useState(false);
        const [loadingGroup, setLoadingGroup] = useState(false);
        const [showRoute, setShowRoute] = useState(false);
        const [error, setError] = useState(null);
        
        // Configurazione raggruppamento
        const [groupingConfig, setGroupingConfig] = useState({
            daysBack: 1,
            enabled: true
        });

        // Formatta valuta
        const formatCurrency = useCallback((amount) => {
            return new Intl.NumberFormat('it-IT', { 
                style: 'currency', 
                currency: 'EUR' 
            }).format(amount || 0);
        }, []);

        // Carica ordini raggruppabili
        const loadGroupableOrders = useCallback(async (days = groupingConfig.daysBack) => {
            if (!groupingConfig.enabled) return;
            
            setLoadingOrders(true);
            setError(null);
            
            try {
                const response = await axios.get('/api/admin/orders/groupable', {
                    params: { days },
                    withCredentials: true
                });
                
                if (response.data.success) {
                    setAvailableOrders(response.data.orders || []);
                    return response.data.orders;
                } else {
                    throw new Error(response.data.message || 'Errore nel caricamento');
                }
            } catch (error) {
                const message = error.response?.data?.message || error.message;
                setError(message);
                console.error('Error loading groupable orders:', error);
                return [];
            } finally {
                setLoadingOrders(false);
            }
        }, [groupingConfig.daysBack, groupingConfig.enabled]);

        // Calcola percorso ottimizzato
        const calculateOptimizedRoute = useCallback(async (orderIds, daysBack) => {
            if (!orderIds || orderIds.length === 0) {
                showToast('Nessun ordine selezionato per il calcolo del percorso', 'warning');
                return null;
            }
            
            setLoadingRoute(true);
            setError(null);
            
            try {
                const response = await axios.post('/api/admin/orders/optimize-route', {
                    orderIds: orderIds,
                    daysBack: daysBack || groupingConfig.daysBack
                }, { withCredentials: true });
                
                if (response.data.success) {
                    setOptimizedRoute(response.data.route);
                    setShowRoute(true);
                    showToast('Percorso ottimizzato calcolato!', 'success');
                    return response.data.route;
                } else {
                    throw new Error(response.data.message || 'Errore nel calcolo del percorso');
                }
            } catch (error) {
                const message = error.response?.data?.message || error.message;
                setError(message);
                console.error('Error calculating route:', error);
                
                if (!error.response) {
                    showToast('Errore di connessione nel calcolo del percorso', 'error');
                } else {
                    showToast(message, 'error');
                }
                return null;
            } finally {
                setLoadingRoute(false);
            }
        }, [groupingConfig.daysBack]);

        // Crea gruppo di ritiro
        const createPickupGroup = useCallback(async (orderIds, route, onSuccess) => {
            if (!orderIds || orderIds.length === 0) {
                showToast('Nessun ordine da raggruppare', 'warning');
                return null;
            }
            
            if (!route) {
                showToast('Calcola prima il percorso ottimizzato', 'warning');
                return null;
            }
            
            setLoadingGroup(true);
            setError(null);
            
            try {
                const response = await axios.post('/api/admin/orders/pickup-groups', {
                    orderIds: orderIds,
                    optimizedRoute: route
                }, { withCredentials: true });
                
                if (response.data.success) {
                    showToast(`Gruppo ritiro creato con ${orderIds.length} ordini`, 'success');
                    
                    // Callback opzionale per notificare il componente padre
                    if (onSuccess && typeof onSuccess === 'function') {
                        onSuccess(response.data.group);
                    }
                    
                    // Ricarica automaticamente gli ordini disponibili
                    await loadGroupableOrders();
                    
                    // Resetta il percorso mostrato dopo la creazione
                    setShowRoute(false);
                    setOptimizedRoute(null);
                    
                    return response.data.group;
                } else {
                    throw new Error(response.data.message || 'Errore nella creazione del gruppo');
                }
            } catch (error) {
                const message = error.response?.data?.message || error.message;
                setError(message);
                console.error('Error creating pickup group:', error);
                showToast(message, 'error');
                return null;
            } finally {
                setLoadingGroup(false);
            }
        }, [loadGroupableOrders]);

        // Aggiorna configurazione giorni
        const updateDaysBack = useCallback(async (days) => {
            setGroupingConfig(prev => ({ ...prev, daysBack: days }));
            await loadGroupableOrders(days);
        }, [loadGroupableOrders]);

        // Abilita/disabilita raggruppamento
        const toggleGrouping = useCallback(async (enabled) => {
            setGroupingConfig(prev => ({ ...prev, enabled }));
            if (enabled) {
                await loadGroupableOrders();
            } else {
                setAvailableOrders([]);
                setOptimizedRoute(null);
                setShowRoute(false);
            }
        }, [loadGroupableOrders]);

        // Resetta tutto
        const resetGrouping = useCallback(() => {
            setAvailableOrders([]);
            setOptimizedRoute(null);
            setShowRoute(false);
            setError(null);
            setLoadingRoute(false);
            setLoadingOrders(false);
            setLoadingGroup(false);
        }, []);

        // Ottieni gli ID degli ordini disponibili
        const getOrderIds = useCallback(() => {
            return availableOrders.map(o => o.id);
        }, [availableOrders]);

    return {
        // State
        availableOrders,
        optimizedRoute,
        showRoute,
        loadingRoute,
        loadingOrders,
        loadingGroup,
        error,
        groupingConfig,
        
        // Actions principali
        loadGroupableOrders,
        calculateOptimizedRoute,
        createPickupGroup,
        
        // Actions configurazione
        updateDaysBack,
        toggleGrouping,
        resetGrouping,
        
        // Helpers
        getOrderIds,
        formatCurrency,
        hasOrders: availableOrders.length > 0,
        totalOrders: availableOrders.length
    };
};


